/**
 * セルフサインアップの悪用対策（R0 §2.1「トライアル悪用の防止策が無い」）。
 *
 * 無料トライアルは「カード不要・送信即開放」だと、共有 IP からスパムを撃つための
 * 使い捨て入口になる。**共有 IP のレピュテーションは 1 度落ちると戻らない**ので、
 * 対策は登録の入口と送信の出口の両方に置く。
 *
 *   入口: 使い捨てメールドメインの拒否 + 同一 IP からの登録回数の上限
 *   出口: `subscriptions.status = 'trialing'` のあいだ送信レートを大幅に絞る
 *
 * ここは**純関数だけ**を置く（Redis や DB を触らない）。判定基準をテストで
 * 固定できるようにするため。実際のカウントは `apps/api` 側で行う。
 */

/* ---------------------------------------------------- 使い捨てメールドメイン */

/**
 * 使い捨て（disposable）メールサービスのドメイン。
 *
 * **網羅は目的ではない。** 数万件のリストを取り込むと、更新が止まった瞬間に
 * 「効いているつもり」になる。ここに置くのは「無料トライアルの自動取得に
 * 実際に使われる代表的なもの」に限り、本命の防御は同一 IP の登録上限と
 * トライアル中の送信レート制限に置く。
 *
 * 追加するときはアルファベット順を保つこと（重複に気付きやすい）。
 */
export const DISPOSABLE_EMAIL_DOMAINS: readonly string[] = [
  '10minutemail.com',
  '20minutemail.com',
  'anonbox.net',
  'burnermail.io',
  'dispostable.com',
  'emailondeck.com',
  'fakeinbox.com',
  'getairmail.com',
  'getnada.com',
  'guerrillamail.com',
  'guerrillamail.info',
  'guerrillamail.net',
  'guerrillamail.org',
  'inboxkitten.com',
  'mailcatch.com',
  'maildrop.cc',
  'mailinator.com',
  'mailnesia.com',
  'mintemail.com',
  'moakt.com',
  'mohmal.com',
  'mytemp.email',
  'sharklasers.com',
  'spam4.me',
  'spamgourmet.com',
  'temp-mail.org',
  'tempail.com',
  'tempinbox.com',
  'tempmail.net',
  'tempmailo.com',
  'throwawaymail.com',
  'trashmail.com',
  'trashmail.de',
  'tuta.io',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
];

const DISPOSABLE_SET = new Set(DISPOSABLE_EMAIL_DOMAINS);

/**
 * メールアドレスまたはドメインが使い捨てサービスかを判定する。
 *
 * サブドメイン（`foo.mailinator.com`）も弾く。使い捨てサービスの多くは
 * 任意のサブドメインを受け付けるので、完全一致だけでは素通りする。
 */
export function isDisposableEmailDomain(emailOrDomain: string): boolean {
  const raw = emailOrDomain.trim().toLowerCase();
  if (raw.length === 0) return false;
  const at = raw.lastIndexOf('@');
  const domain = (at >= 0 ? raw.slice(at + 1) : raw).replace(/\.+$/, '');
  if (domain.length === 0) return false;
  if (DISPOSABLE_SET.has(domain)) return true;

  const labels = domain.split('.');
  for (let i = 1; i < labels.length - 1; i += 1) {
    if (DISPOSABLE_SET.has(labels.slice(i).join('.'))) return true;
  }
  return false;
}

/* --------------------------------------------------------- 同一 IP の登録上限 */

/**
 * 同一 IP からの申込受理数の上限（24 時間）。
 *
 * レート制限（`RATE_DEFAULTS.signup` = 5 回 / 1 時間）とは目的が違う。
 * あちらは「連打」を止めるもので、1 日かけて 1 時間おきに 24 件作られると素通りする。
 * こちらは**成功した申込の件数**を数えるので、時間をかけた大量取得を止められる。
 */
export const SIGNUP_IP_MAX_PER_DAY = 3;
export const SIGNUP_IP_WINDOW_SECONDS = 24 * 60 * 60;

/** メール到達確認トークンの有効期間。 */
export const SIGNUP_VERIFY_TTL_SECONDS = 24 * 60 * 60;

/** 確認メールの再送を受け付ける最短間隔（秒）。 */
export const SIGNUP_RESEND_MIN_INTERVAL_SECONDS = 60;

/* ------------------------------------------------- トライアル中の送信レート */

/**
 * トライアル中の送信上限。**プランの上限とは別に、必ずこちらが優先される。**
 *
 * 50 通 / 日 は「動作確認には十分だがスパム配信には全く足りない」水準。
 * ここを緩めるとトライアルの意味が「無料の送信枠」に変わる。
 */
export const TRIAL_SEND_PER_HOUR_MAX = 20;
export const TRIAL_SEND_PER_DAY_MAX = 50;

export interface SendLimits {
  hour: number;
  day: number;
}

/**
 * 契約状態に応じた実効送信上限を返す。
 *
 * `trialing` のときだけ絞る。`active` に変わった瞬間にプランの上限へ戻るので、
 * §2.2 が実装したトライアル終了処理（`subscription.trial-close`）が走れば
 * 追加の操作なしで解放される。
 */
export function effectiveSendLimits(
  subscriptionStatus: string | null | undefined,
  planLimits: SendLimits,
): SendLimits {
  if (subscriptionStatus !== 'trialing') return planLimits;
  return {
    hour: Math.min(planLimits.hour, TRIAL_SEND_PER_HOUR_MAX),
    day: Math.min(planLimits.day, TRIAL_SEND_PER_DAY_MAX),
  };
}
